class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Listen for keyup events, if the 'Escape' key is pressed, close the cart
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Listen for click events on the overlay, if clicked, close the cart
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    // Set accessibility attributes for the header cart icon
    this.setHeaderCartIconAccessibility();
  }

  // Set accessibility attributes for the header cart icon
  setHeaderCartIconAccessibility() {
    // Find the cart icon link
    const cartLink = document.querySelector('#cart-icon-bubble');
    // Set role and aria attributes
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // Listen for click events on the cart icon link
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    // Listen for keydown events on the cart icon link
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // Open the cart
  open(triggeredBy) {
    // If triggered by an element, set it as active
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Find the cart drawer note and set accessibility if necessary
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Set animation classes and activate the cart drawer
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // After transition ends, trap focus within the cart drawer
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Prevent scrolling of the body
    document.body.classList.add('overflow-hidden');
  }

  // Close the cart
  close() {
    // Remove active class
    this.classList.remove('active');
    // Remove focus trapping
    removeTrapFocus(this.activeElement);
    // Allow scrolling of the body
    document.body.classList.remove('overflow-hidden');
  }

  // Set accessibility attributes for the cart drawer note
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    // Toggle aria-expanded on click
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    // Listen for keyup events on the parent element
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Render the contents of the cart drawer
  renderContents(parsedState) {
    // Remove empty class if present
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // Set product ID
    this.productId = parsedState.id;
    // Render sections of the cart drawer
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Set up event listeners and open the cart drawer
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      
      // Reinitialize components after DOM replacement (renderContents replaces innerHTML)
      if (window.cartDrawerInitManager) {
        window.cartDrawerInitManager.scheduleReinit();
        // Trigger animation for new cart items
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (window.cartDrawerInitManager && typeof window.cartDrawerInitManager.animateNewCartItems === 'function') {
              window.cartDrawerInitManager.animateNewCartItems();
            }
          }, 100);
        });
      }
      
      this.open();
    });
  }

  // Get the inner HTML of a section
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Get sections to render in the cart drawer
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  // Get DOM of a section
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  // Set the active element
  setActiveElement(element) {
    this.activeElement = element;
  }
}

// Define the custom element 'cart-drawer'
customElements.define('cart-drawer', CartDrawer);

// Define CartDrawerItems extending CartItems
class CartDrawerItems extends CartItems {
  // Get sections to render in the cart drawer
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

// Define the custom element 'cart-drawer-items'
customElements.define('cart-drawer-items', CartDrawerItems);